import React, { useMemo, useState } from 'react';
import { Alert, Button, Paper, Stack, TextField, Typography } from '@mui/material';

const REQUIRED_MANUAL_RECOMPUTE_SCOPES = ['report', 'bulk_bac_program', 'bac_program'];

const normalizeString = (value) => {
  if (value === null || value === undefined) return '';
  return String(value).trim();
};

export const supportsManualRecompute = (dataset) => {
  const scopes = Array.isArray(dataset?.recompute?.scopes) ? dataset.recompute.scopes : [];
  return Boolean(
    dataset?.recompute?.enabled
      && REQUIRED_MANUAL_RECOMPUTE_SCOPES.every((scope) => scopes.includes(scope))
  );
};

export const buildRecomputeSlicesFromRows = (rows = []) => {
  const byKey = new Map();
  rows.forEach((row) => {
    const bac = normalizeString(row?.bac || row?.['Dealer BAC'] || row?.BAC);
    if (!bac) return;
    const programName = normalizeString(
      row?.programName || row?.__reportProgram || row?.__appsheetProgram || row?.__dpeProgram || row?.Program || row?.program
    );
    if (!programName) return;
    const key = `${bac}::${programName.toLowerCase()}`;
    if (!byKey.has(key)) byKey.set(key, { bac, programName });
  });
  return Array.from(byKey.values());
};

const ReportRecomputeControls = ({ dataset, selectedRows, busy = false, onRecompute }) => {
  const [bac, setBac] = useState('');
  const [programName, setProgramName] = useState('');
  const selectedSlices = useMemo(() => buildRecomputeSlicesFromRows(selectedRows), [selectedRows]);

  if (!supportsManualRecompute(dataset)) return null;

  const canSubmitSingle = normalizeString(bac) && normalizeString(programName) && !busy;
  const canSubmitBulk = selectedSlices.length > 0 && !busy;

  const handleSingleSubmit = (event) => {
    event.preventDefault();
    if (!canSubmitSingle) return;
    onRecompute({
      level: 'bac_program',
      slices: [{ bac: normalizeString(bac), programName: normalizeString(programName) }],
    });
  };

  return (
    <Paper variant="outlined" sx={{ p: 2, borderRadius: 3 }}>
      <Stack spacing={1.5}>
        <Stack spacing={0.5}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            Manual recompute fallback
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Rebuilds this modular report from the local AppSheet/DPE caches. It does not pull fresh upstream data.
          </Typography>
        </Stack>
        <Alert severity="info" variant="outlined">
          Row changes made from report actions should recompute automatically. Use these controls when you need to force
          the backend report materialization to catch up.
        </Alert>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} flexWrap="wrap">
          <Button
            variant="outlined"
            onClick={() => onRecompute({ level: 'report' })}
            disabled={busy}
            sx={{ textTransform: 'none' }}
          >
            Recompute full report
          </Button>
          <Button
            variant="outlined"
            onClick={() => onRecompute({ level: selectedSlices.length === 1 ? 'bac_program' : 'bulk_bac_program', slices: selectedSlices })}
            disabled={!canSubmitBulk}
            sx={{ textTransform: 'none' }}
          >
            Recompute selected BAC/programs
          </Button>
          <Typography variant="body2" color="text.secondary" sx={{ alignSelf: { md: 'center' } }}>
            {selectedSlices.length} selected BAC/program slice(s)
          </Typography>
        </Stack>
        <Stack
          component="form"
          direction={{ xs: 'column', md: 'row' }}
          spacing={1}
          alignItems={{ xs: 'stretch', md: 'center' }}
          onSubmit={handleSingleSubmit}
        >
          <TextField
            label="BAC"
            value={bac}
            onChange={(event) => setBac(event.target.value)}
            size="small"
            required
            inputProps={{ 'data-testid': 'manual-recompute-bac' }}
          />
          <TextField
            label="Program"
            value={programName}
            onChange={(event) => setProgramName(event.target.value)}
            size="small"
            required
            inputProps={{ 'data-testid': 'manual-recompute-program' }}
          />
          <Button type="submit" variant="contained" disabled={!canSubmitSingle} sx={{ textTransform: 'none' }}>
            Recompute one BAC/program
          </Button>
        </Stack>
      </Stack>
    </Paper>
  );
};

export default ReportRecomputeControls;
